import Badge from "@/common/components/Badge";
import Box from "@/common/components/Box";
import { CurrencyTextField } from "@/common/components/CurrencyTextField";
import { UnitTextField } from "@/common/components/UnitTextField";
import { unities } from "@/common/constants/unities.constants";
import { formatCurrency, formatDate, formatDecimal, formatUnit } from "@/common/utils/formatters";
import { FileBoxIcon } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { NegotiationValidationSchema } from "../../schemas/negotiation.schema";
import { capitalizeFirstLetter } from "@/common/utils/capitalizeFirstLetter";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { NegotiationAvailableProductData } from "../../types/applicableProduct.type";

interface NegotiatingProductItemProps {
  index: number;
  onDelete: () => void;
  product: NegotiationAvailableProductData;
}

const NegotiatingProductItem = ({ index, onDelete, product }: NegotiatingProductItemProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<NegotiationValidationSchema>();

  return (
    <Box className="flex flex-col gap-4">
      <Badge
        Icon={FileBoxIcon}
        text={product.display_name}
        onDelete={onDelete}
        className="rounded-sm flex bg-neutral-op-8 w-fit flex-row-reverse overflow-hidden !p-0 gap-0"
        overwriteClassName
        deleteClassName="bg-black p-2"
        contentClassName="px-3 py-2"
        deleteIconColor="var(--foreground)"
      />

      <div className="flex flex-col gap-2">
        <CurrencyTextField
          markAsRequired
          label="Valor desejado"
          {...register(`negotiation_products_attributes.${index}.value`)}
          errorMessage={errors.negotiation_products_attributes?.[index]?.value?.message || undefined}
        />

        <div className="flex items-center justify-end gap-1">
          <p className="italic text-sm font-light">
            Valor no estabelecimento:
          </p>

          {product.price_value ? (
            <p className="text-sm font-bold flex items-center gap-1">
              {formatCurrency(Number(product.price_value))}
              {product.unit_for_quantity && `/${unities[product.unit_for_quantity]}`}

              {product.price_updated_at && (
                <span className="italic text-xs font-light">
                  ({formatDate(new Date(product.price_updated_at))})
                </span>
              )}
            </p>
          ) : (
            <p className="text-sm text-white-300">Não informado</p>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <UnitTextField
          markAsRequired
          decimals={0}
          label="Estimativa de consumo mensal"
          unit={`${product.unit_for_quantity ? unities[product.unit_for_quantity] : ''}/Mês`}
          {...register(`negotiation_products_attributes.${index}.quantity`)}
          errorMessage={errors.negotiation_products_attributes?.[index]?.quantity?.message || undefined}
        />

        <div className="flex justify-end items-center gap-1">
          <p className="italic text-sm font-light">
            Total consumido em {capitalizeFirstLetter(format(new Date(), 'MMMM yyyy', { locale: ptBR }))}:
          </p>

          <p className="text-sm font-bold">
            {formatUnit(formatDecimal(product.total_consumed_last_month || 0, 0), product.unit_for_quantity || '')}
          </p>
        </div>
      </div>
    </Box>
  );
};

export default NegotiatingProductItem;
